import { FC, useState, useRef } from "react";
import { Modal, Upload, message, Progress, Typography, Button } from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    FileOutlined,
    StopOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import {
    ProjectType,
    allowedExtensions,
    hasAllowedExtension,
    isHiddenOrSystemFile,
} from "../../../types/ProjectTypes";
import { ReactComponent as FolderGradientIcon } from "../../../assets/icons/svg/folder-gradient.svg";
import { ReactComponent as FolderBlueIcon } from "../../../assets/icons/svg/folder-blue.svg";
import { handleError } from "../../../common/ErrorHandling";
import InfinitySpinner from "../../../common/components/InfinitySpinner";
import { createProject, uploadProjectDocument } from "../../../common/api/ProjectClient";

const { Dragger } = Upload;
const { Text } = Typography;

interface ModalUploadProjectFilesProps {
    open: boolean;
    defaultProjectType: ProjectType;
    onClose: () => void;
    onComplete: () => void;
    onUploadComplete?: () => void;
}

interface FileStatus {
    file: RcFile;
    status: "invalid" | "pending" | "success" | "error";
    error?: string; // error from server when uploading document
}

interface DirectoryStatus {
    name: string;
    status: "pending" | "uploading" | "success" | "error";
    error?: string; // error from server when creating the project
    projectId?: number; // project ID in case of success
    files: FileStatus[];
}

/**
 * Organize all the files that are going to be uploaded into directories/projects.
 *
 * If a directory contains at least one file, it will be a project.
 * If a directory only contains subdirectories, we will create one project per subdirectory.
 *
 * Silently skip hidden/system files.
 * Mark unallowed files to indicate to the user that they won't be uploaded.
 * Skip empty directories.
 * Skip directories that have already been dragged.
 *
 * @param files
 * @returns A map of all directories with their files.
 */
const groupFilesByDirectory = (files: RcFile[]): DirectoryStatus[] => {
    // First pass: group by top-level directory
    const directoryMap = new Map<string, FileStatus[]>();
    files.forEach((file) => {
        // Silently skip hidden and system files
        if (isHiddenOrSystemFile(file.name)) return;

        // webkitRelativePath contains the full path like "dirname/subdir/file.pdf"
        // See https://developer.mozilla.org/en-US/docs/Web/API/File/webkitRelativePath
        const relativePath = (file as any).webkitRelativePath || file.name;
        const parts = relativePath.split("/");

        // The first part is the top-level directory name
        // We may not have the directory name if `webkitRelativePath` is not supported
        const directoryName = parts.length > 1 ? parts[0] : "Unnamed Project";

        if (!directoryMap.has(directoryName)) {
            directoryMap.set(directoryName, []);
        }

        directoryMap.get(directoryName)!.push({
            file,
            status: hasAllowedExtension(file.name) ? "pending" : "invalid",
        });
    });

    // Second pass: check if any top-level directory contains only subfolders (no direct files)
    // If so, split it into separate projects for each subfolder
    const finalDirectoryMap = new Map<string, DirectoryStatus>();
    directoryMap.forEach((filesWithStatus, topLevelDir) => {
        // Check if all files are in subdirectories (path has at least 3 parts: topDir/subDir/file)
        const hasDirectFiles = filesWithStatus.some((fs) => {
            const relativePath = (fs.file as any).webkitRelativePath || fs.file.name;
            const parts = relativePath.split("/");
            return parts.length <= 2; // topDir/file.ext means direct file or file in "Unnamed Project"
        });

        if (hasDirectFiles) {
            // Has direct files, keep as single project
            finalDirectoryMap.set(topLevelDir, {
                name: topLevelDir,
                status: "pending",
                files: filesWithStatus,
            });
        } else {
            // No direct files, split by second-level directory
            // Prefix with parent directory name for context
            filesWithStatus.forEach((fs) => {
                const relativePath = (fs.file as any).webkitRelativePath || fs.file.name;
                const parts = relativePath.split("/");

                // Use "ParentDir - SubDir" as the project name
                const projectName = `${topLevelDir} - ${parts[1]}`;
                if (!finalDirectoryMap.has(projectName)) {
                    finalDirectoryMap.set(projectName, {
                        name: projectName,
                        status: "pending",
                        files: [],
                    });
                }
                finalDirectoryMap.get(projectName)!.files.push(fs);
            });
        }
    });

    return Array.from(finalDirectoryMap.values());
};

/** A short, user-facing reason for a failed document upload. */
const uploadErrorReason = (error: any): string =>
    error?.code === 400 ? "File type not supported" : "Upload failed";

export const ModalUploadProjectFiles: FC<ModalUploadProjectFilesProps> = ({
    open,
    defaultProjectType,
    onClose,
    onComplete,
    onUploadComplete,
}) => {
    const [directories, setDirectories] = useState<DirectoryStatus[]>([]);
    const [uploading, setUploading] = useState(false);

    // Accumulate files since beforeUpload is called once per file
    const pendingFilesRef = useRef<RcFile[]>([]);
    const processTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Helper to update a directory status
    const updateDirStatus = (name: string, f: (ds: DirectoryStatus) => DirectoryStatus) => {
        setDirectories((prev) => prev.map((ds) => (ds.name === name ? f(ds) : ds)));
    };

    // Helper to update a file status
    const updateFileStatus = (
        dirName: string,
        fileName: string,
        f: (fs: FileStatus) => FileStatus
    ) => {
        updateDirStatus(dirName, (ds) => ({
            ...ds,
            files: ds.files.map((fs) => (fs.file.name === fileName ? f(fs) : fs)),
        }));
    };

    const processAccumulatedFiles = () => {
        const allFiles = pendingFilesRef.current;
        pendingFilesRef.current = [];

        if (allFiles.length === 0) {
            return;
        }

        const dirsWithStatus = groupFilesByDirectory(allFiles);

        // Check if any directory has uploadable files
        const hasUploadableFiles = dirsWithStatus.some((ds) =>
            ds.files.some((fs) => fs.status === "pending")
        );

        if (!hasUploadableFiles) {
            message.error("No valid document files found in the selected directory.");
            return;
        }

        // Merge with existing directories
        setDirectories((prev) => {
            const existingNames = new Set(prev.map((ds) => ds.name));
            const newDirsWithStatus = dirsWithStatus.filter((ds) => !existingNames.has(ds.name));
            const duplicateCount = dirsWithStatus.length - newDirsWithStatus.length;

            if (duplicateCount > 0) {
                message.warning(
                    `${duplicateCount} director${duplicateCount > 1 ? "ies" : "y"} already added, skipped.`
                );
            }

            return [...prev, ...newDirsWithStatus];
        });
    };

    const handleBeforeUpload = (file: RcFile) => {
        // Accumulate files
        pendingFilesRef.current.push(file);

        // Clear any existing timeout and set a new one
        // This batches all files together since beforeUpload is called rapidly for each file
        if (processTimeoutRef.current) {
            clearTimeout(processTimeoutRef.current);
        }
        processTimeoutRef.current = setTimeout(() => {
            processAccumulatedFiles();
            processTimeoutRef.current = null;
        }, 100);

        // Prevent automatic upload - we'll handle it manually
        return false;
    };

    const uploadProjects = async () => {
        if (directories.length === 0) return;

        setUploading(true);

        for (const dir of directories) {
            // Update status to uploading
            updateDirStatus(dir.name, (ds) => ({ ...ds, status: "uploading" }));
            try {
                // Create a draft project for this directory.
                const project = await createProject({ name: dir.name, type: defaultProjectType });

                // Upload each valid file to the project's latest analysis and
                // report its status independently, so one bad file doesn't sink
                // the others.
                for (const { file } of dir.files.filter((f) => f.status === "pending")) {
                    try {
                        await uploadProjectDocument(project.id, file);
                        updateFileStatus(dir.name, file.name, (fs) => ({
                            ...fs,
                            status: "success",
                        }));
                    } catch (error) {
                        // Show the reason next to the file rather than a global
                        // toast. ILErrors carry the detail on `misc`; fall back to
                        // the generic message.
                        updateFileStatus(dir.name, file.name, (fs) => ({
                            ...fs,
                            status: "error",
                            error: uploadErrorReason(error),
                        }));
                    }
                }

                // Update project status
                updateDirStatus(dir.name, (ds) => ({
                    ...ds,
                    status: "success",
                    projectId: project.id,
                }));
            } catch (error) {
                handleError(error);
                updateDirStatus(dir.name, (ds) => ({
                    ...ds,
                    status: "error",
                    error: error instanceof Error ? error.message : "Upload failed",
                }));
            }
        }

        setUploading(false);
        onUploadComplete?.();
    };

    const handleRemoveDirectory = (dirName: string) => {
        setDirectories((prev) => prev.filter((d) => d.name !== dirName));
    };

    const handleClose = () => {
        if (!uploading) {
            setDirectories([]);
            onClose();
        }
    };

    const handleDone = () => {
        setDirectories([]);
        onComplete();
    };

    const hasDirectories = directories.length > 0;

    const allComplete =
        directories.length > 0 &&
        directories.every((r) => r.status === "success" || r.status === "error");

    const renderHeader = () => {
        const cnt = allComplete
            ? directories.filter((ds) => ds.status === "success").length
            : directories.length;
        let pronoun = cnt > 1 ? "them" : "it";
        return (
            <h3>
                {cnt} {allComplete ? "draft " : ""}project{cnt > 1 ? "s" : ""}{" "}
                {allComplete ? "created" : "ready to upload"}
                {allComplete && cnt > 0 && `. Review ${pronoun} and submit for analysis.`}
            </h3>
        );
    };

    /* Render the directory that will be uploaded and turned into a project. */
    const renderDirectory = (ds: DirectoryStatus) => {
        const filesToUpload = ds.files.filter((fs) => fs.status !== "invalid").length;
        const filesProcessed = ds.files.filter(
            (fs) => fs.status === "success" || fs.status === "error"
        ).length;

        return (
            <>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        margin: "16px 2px 8px 2px",
                        backgroundColor: "#eee",
                    }}
                >
                    <FolderBlueIcon style={{ marginLeft: "5px" }} />

                    <Text strong style={{ flex: 1 }}>
                        {ds.name}
                    </Text>

                    {ds.status === "pending" && (
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<CloseOutlined style={{ color: "#ff4d4f" }} />}
                            style={{ marginRight: "10px" }}
                            onClick={() => handleRemoveDirectory(ds.name)}
                        />
                    )}

                    {ds.status === "uploading" && (
                        <Text type="secondary">
                            ({filesProcessed} / {filesToUpload})
                        </Text>
                    )}

                    {ds.status === "success" && (
                        <CheckCircleOutlined style={{ color: "#52c41a", marginRight: "10px" }} />
                    )}

                    {ds.status === "error" && (
                        <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: "10px" }} />
                    )}
                </div>

                {ds.status === "uploading" && (
                    <Progress
                        percent={Math.round((filesProcessed / filesToUpload) * 100)}
                        size="small"
                        style={{ marginBottom: 8 }}
                    />
                )}
            </>
        );
    };

    /* Render a valid file and its upload status. */
    const renderValidFile = ({ file, status, error }: FileStatus) => (
        <>
            {status === "pending" ? (
                <FileOutlined style={{ color: "#1890ff" }} />
            ) : status === "success" ? (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
                status === "error" && <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
            )}
            <Text>{file.name}</Text>
            {status === "error" && error && (
                <Text type="danger" style={{ fontSize: 12 }}>
                    {error}
                </Text>
            )}
        </>
    );

    /* Render an invalid file (before upload start) */
    const renderInvalidFile = ({ file }: FileStatus) => (
        <>
            <StopOutlined style={{ color: "#d9d9d9" }} />
            <Text type="secondary" style={{ textDecoration: "line-through" }}>
                {file.name}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
                (unsupported)
            </Text>
        </>
    );

    /* Render the list of directories and their status. */
    const renderDirectoriesList = () => (
        <div>
            {directories.length > 0 && renderHeader()}

            <div style={{ maxHeight: 400, overflowY: "auto" }}>
                {directories.map((ds) => {
                    return (
                        <div key={ds.name} style={{ marginBottom: 16 }}>
                            {renderDirectory(ds)}

                            <div style={{ paddingLeft: 28 }}>
                                {ds.files.map((fs) => (
                                    <div
                                        key={(fs.file as any).webkitRelativePath || fs.file.name}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "2px 0",
                                        }}
                                    >
                                        {/* show valid files and their status after uploading */}
                                        {fs.status !== "invalid" && renderValidFile(fs)}
                                        {/* show invalid files only when preparing the files to be uploaded to alert the user */}
                                        {fs.status === "invalid" &&
                                            !allComplete &&
                                            renderInvalidFile(fs)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <Modal
            title="Upload your project files"
            open={open}
            onCancel={handleClose}
            okText={allComplete ? "Done" : "Upload"}
            onOk={allComplete ? handleDone : uploadProjects}
            okButtonProps={{
                disabled: !hasDirectories || uploading,
                loading: uploading,
            }}
            cancelButtonProps={{
                disabled: uploading,
                style: allComplete ? { display: "none" } : undefined,
            }}
            width="900px"
        >
            {!allComplete && (
                <Dragger
                    multiple
                    directory
                    beforeUpload={handleBeforeUpload}
                    showUploadList={false}
                    openFileDialogOnClick={!uploading}
                    disabled={uploading}
                    height={150}
                    style={{
                        width: "100%",
                        color: "#1890FF",
                        borderRadius: "4px",
                        background: "#141E2E",
                        fontSize: 16,
                        fontWeight: 600,
                        marginBottom: "20px",
                    }}
                >
                    {uploading ? (
                        <>
                            <InfinitySpinner style={{ margin: "8px auto 4px" }} />
                            <p style={{ color: "#ffffff", fontSize: 16, fontWeight: 600 }}>
                                Your projects are being created. Just a moment...
                            </p>
                        </>
                    ) : (
                        <>
                            <FolderGradientIcon style={{ margin: "8px auto 4px" }} />
                            <p style={{ color: "#ffffff", fontSize: 16, fontWeight: 600 }}>
                                Drop or upload directories here
                            </p>
                        </>
                    )}
                </Dragger>
            )}
            {!hasDirectories ? (
                <>
                    <h3>When uploading directories</h3>
                    <p className="ant-upload-hint">
                        A single project directory contains all documents for one project
                        (contracts, MSAs, amendments, etc.) and{" "}
                        <strong>becomes a single project</strong>.
                    </p>
                    <p className="ant-upload-hint">
                        A single vendor directory containing multiple project directories will
                        create a <strong>separate project for each project directory</strong>.
                    </p>
                    <p className="ant-upload-hint" style={{ marginTop: 8 }}>
                        All supported documents inside each project directory will be uploaded
                        automatically. <br />
                        <span style={{ color: "grey" }}>{allowedExtensions.join(", ")}</span>
                    </p>
                </>
            ) : (
                renderDirectoriesList()
            )}
        </Modal>
    );
};
